import { payerTypeConstants } from "constants/order";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { isNoMaximumOOPDollars } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import { useState, useMemo, useEffect, useRef } from "react";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IFacilityRules, INonFacilityRules } from "types/Order/Order";

import { facilityBenefitsInfo, professionalBenefitsInfo } from "../../helpers";
import { MatchingOrders } from "./components/MatchingOrders";
import { PatientBenefits } from "./components/PatientBenefits";
import { ReportBenefitsModal } from "./components/ReportBenefitsModal";

/**
 * Benefit fields that auto-open the report modal when a SuperAdmin changes them away
 * from their value when editing started. `formKey` is the form field / input id;
 * `field` is the backend BenefitField enum value.
 */
const AUTO_DETECT_BENEFIT_FIELDS = [
  { formKey: "deductible", field: "Deductible" },
  { formKey: "outOfPocketMax", field: "OutOfPocketMax" },
  { formKey: "flatCopay", field: "FlatCopay" },
  { formKey: "coinsurancePercent", field: "CoinsurancePercent" },
] as const;

const StyledInputNumber = styled(InputNumber)`
  input {
    max-width: 100%;
  }
`;

const StyledCheckboxContainer = styled.div<{ $extraTopSpace?: boolean }>`
  @media (min-width: 768px) {
    margin-top: ${({ $extraTopSpace }) => ($extraTopSpace ? "125px" : "44px")};
  }
`;

const StyledCoinsuranceCheckboxContainer = styled.div<{ $compact?: boolean }>`
  @media (min-width: 768px) {
    margin-top: ${({ $compact }) => ($compact ? "44px" : "60px")};
  }
`;

const StyledTooltip = styled(Tooltip)`
  .p-tooltip-text {
    background-color: white !important;
  }
`;

const StyledBenefitsContainer = styled.div`
  border-bottom: 1px solid var(--gray-new-200);
`;

const StyledBenefitsContainerLeft = styled.div`
  @media (min-width: 768px) {
    width: 60% !important;
  }
`;

const StyledBenefitsContainerRight = styled.div`
  @media (min-width: 768px) {
    width: 20% !important;
  }
`;

export function Benefits({
  account,
  draft,
  redo,
  copy,
  costApplicationRules,
  eligibilityMessage,
}: {
  account: IAccount;
  draft?: boolean;
  redo?: boolean;
  copy?: boolean;
  costApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
  eligibilityMessage?: string | null;
}) {
  const [hideBenefits, setHideBenefits] = useState(false);
  const [showFacilityBenefits, setShowFacilityBenefits] = useState(
    account.costApplicationRulesVisible.facilityRules
  );
  const [showProfessionalBenefits, setShowProfessionalBenefits] = useState(
    account.costApplicationRulesVisible.nonFacilityRules
  );

  const { watch, control, trigger, setValue, getValues } =
    useFormContext<FieldValues>();

  // Initialize showProfessionalCopay based on existing form data
  const formShowProfessionalCopay = watch("showProfessionalCopay");
  const professionalFlatCopayValue = watch("professionalFlatCopay");

  const [showProfessionalCopay, setShowProfessionalCopay] = useState(
    formShowProfessionalCopay ||
      (professionalFlatCopayValue && professionalFlatCopayValue > 0) ||
      false
  );

  // Progressive deductible state
  const [reductionsApplied, setReductionsApplied] = useState(false);
  const [prevDeduct, setPrevDeduct] = useState<number>(0);
  const [prevOop, setPrevOop] = useState<number>(0);
  const benefitDetailsField = watch("benefitDetails");
  const payerNameField = watch("payerName");
  const copayField = watch("flatCopay");
  const deductibleField = watch("deductible");
  const coinsuranceField = watch("coinsurancePercent");
  const outOfPocketMaxField = watch("outOfPocketMax");
  const professionalFlatCopayField = watch("professionalFlatCopay");
  const draftOrderFirstRenderField = watch("draftOrderFirstRender");
  const redoOrderFirstRenderField = watch("redoOrderFirstRender");
  const copyOrderFirstRenderField = watch("copyOrderFirstRender");

  const { userInfo } = useUserWithAccount();
  const isSuperAdmin = userInfo?.scope === UserRole.SUPER_ADMIN;

  // Benefit report modal (CLE-2365). Opens via the Report button or auto-detect.
  const [reportModal, setReportModal] = useState<{
    visible: boolean;
    field?: string;
    source: "MANUAL_BUTTON" | "AUTO_DETECT";
    autoValue?: number | null;
    correctedValue?: number | null;
  }>({ visible: false, source: "MANUAL_BUTTON" });
  // Fields already auto-prompted this session (so we don't re-nag the same field).
  const autoPromptedFields = useRef<Set<string>>(new Set());
  // Each benefit field's value captured on focus, i.e. BEFORE the user edits it.
  // This is the reliable "previous value" — benefitDetails is often null in redo.
  const fieldBaseline = useRef<Record<string, unknown>>({});

  const snapshotBenefitField = (id: string) => {
    if (AUTO_DETECT_BENEFIT_FIELDS.some((f) => f.formKey === id)) {
      fieldBaseline.current[id] = getValues(id);
    }
  };

  // Auto-detect: when a SuperAdmin changes a benefit field away from its value when
  // they started editing it, open the report modal once (with the before/after).
  const maybeReportBenefitChange = (formKey: string) => {
    if (!isSuperAdmin) return;
    const cfg = AUTO_DETECT_BENEFIT_FIELDS.find((f) => f.formKey === formKey);
    if (!cfg) return;
    const before = fieldBaseline.current[formKey];
    const after = getValues(formKey);
    if (before == null || before === "") return; // only when there was a prior value
    if (after == null || after === "") return;
    if (Number(before) === Number(after)) return; // unchanged
    if (autoPromptedFields.current.has(formKey)) return; // already prompted
    autoPromptedFields.current.add(formKey);
    setReportModal({
      visible: true,
      field: cfg.field,
      source: "AUTO_DETECT",
      autoValue: Number(before),
      correctedValue: Number(after),
    });
  };

  // Fields for reduction functionality
  const deductionSuggested = watch("deductibleReductionAmount") || 0;
  const oopSuggested = watch("oopReductionAmount") || 0;
  const hasMatchingOrders = watch("hasMatchingOrders") || false;
  const matchingOrdersData = watch("matchingOrdersData") || [];
  const outOfPocketMaxValue = watch("outOfPocketMax");
  const isRemainingOutOfPocketMaxInfinity =
    watch("isRemainingOutOfPocketMaxInfinity") ||
    isNoMaximumOOPDollars(outOfPocketMaxValue);

  const { handleOnBlurField } = useFormValidation();
  const { payersTypeInfo } = useSelector((state: any) => state.orderSlice);

  // Apply deductible and OOP reductions
  const applyReductions = () => {
    const currentDeductible = getValues("deductible") || 0;
    const currentOop = getValues("outOfPocketMax") || 0;
    // Store original values for potential undo
    setPrevDeduct(currentDeductible);
    setPrevOop(currentOop);

    // Apply reductions
    setValue("deductible", Math.max(0, currentDeductible - deductionSuggested));
    setValue("outOfPocketMax", Math.max(0, currentOop - oopSuggested));

    // Mark as applied
    setReductionsApplied(true);
    setValue("reductionsApplied", true);

    // Validate fields after changes
    trigger(["deductible", "outOfPocketMax"]);
  };

  // Restore original values
  const undoReductions = () => {
    setValue("deductible", prevDeduct);
    setValue("outOfPocketMax", prevOop);
    setReductionsApplied(false);
    setValue("reductionsApplied", false);
    trigger(["deductible", "outOfPocketMax"]);
  };

  // Reset reduction applied state when new suggestions arrive
  useEffect(() => {
    if (reductionsApplied && (deductionSuggested > 0 || oopSuggested > 0)) {
      setReductionsApplied(false);
      setValue("reductionsApplied", false);
    }
  }, [deductionSuggested, oopSuggested]);

  useEffect(() => {
    const facilityRulesVisibleForAccount =
      account.costApplicationRulesVisible.facilityRules;
    const nonFacilityRulesVisibleForAccount =
      account.costApplicationRulesVisible.nonFacilityRules;
    const payerType = payersTypeInfo.find(
      (payer: { name: string; type: string }) => payer.name === payerNameField
    )?.type;
    if (!payerType) return;
    let fieldValues = {
      facilityCopay: true,
      facilityDeductible: true,
      facilityCoinsurance: true,
      professionalCopay: true,
      professionalDeductible: true,
      professionalCoinsurance: true,
    };

    if (payerNameField === "Self-Pay Rate") {
      setShowFacilityBenefits(false);
      setShowProfessionalBenefits(false);
      setHideBenefits(true);
    } else {
      setShowFacilityBenefits(facilityRulesVisibleForAccount);
      setShowProfessionalBenefits(nonFacilityRulesVisibleForAccount);
      setHideBenefits(false);
      if (
        ((draft && draftOrderFirstRenderField) ||
          (redo && redoOrderFirstRenderField) ||
          (copy && copyOrderFirstRenderField)) &&
        !!costApplicationRules
      ) {
        if (!facilityRulesVisibleForAccount) {
          setValue("facilityCopay", true);
          setValue("facilityDeductible", true);
          setValue("facilityCoinsurance", true);
        }
        if (!nonFacilityRulesVisibleForAccount) {
          setValue("professionalCopay", true);
          setValue("professionalDeductible", true);
          setValue("professionalCoinsurance", true);
        }
        return;
      }
      if (
        ((draft && draftOrderFirstRenderField) ||
          (redo && redoOrderFirstRenderField) ||
          (copy && copyOrderFirstRenderField)) &&
        !costApplicationRules
      ) {
        Object.entries(fieldValues).forEach(([field, value]) => {
          setValue(field, value);
        });
        return;
      }
      if (payerType === payerTypeConstants.MEDICARE_ADVANTAGE) {
        fieldValues = {
          ...fieldValues,
          facilityDeductible: true,
          facilityCoinsurance: facilityRulesVisibleForAccount
            ? !(copayField > 0)
            : true,
          professionalDeductible: true,
          professionalCoinsurance: nonFacilityRulesVisibleForAccount
            ? !(copayField > 0)
            : true,
        };
      }
    }

    Object.entries(fieldValues).forEach(([field, value]) => {
      setValue(field, value);
    });
  }, [payerNameField, copayField, payersTypeInfo, draft, copy, redo]);

  // Sync showProfessionalCopay state with form values
  useEffect(() => {
    if (
      formShowProfessionalCopay ||
      (professionalFlatCopayValue && professionalFlatCopayValue > 0)
    ) {
      setShowProfessionalCopay(true);
      // Set default checkbox values when showing based on existing data
      if (
        !getValues("facilityProfessionalCopay") &&
        !getValues("professionalProfessionalCopay")
      ) {
        setValue("facilityProfessionalCopay", false);
        setValue("professionalProfessionalCopay", true);
      }
    }
  }, [
    formShowProfessionalCopay,
    professionalFlatCopayValue,
    setValue,
    getValues,
  ]);

  // Handle professional copay automation from eligibility response
  useEffect(() => {
    const handleProfessionalCopayTrigger = (event: CustomEvent) => {
      // Show professional copay field if API returns a value > 0 AND nonFacilityRules is enabled
      if (
        event.detail?.value > 0 &&
        account.costApplicationRulesVisible.nonFacilityRules
      ) {
        setShowProfessionalCopay(true);
        setValue("showProfessionalCopay", true);
        // Only uncheck professional copay if professional benefits are enabled
        if (showProfessionalBenefits) {
          setValue("professionalCopay", false);
          trigger("professionalCopay");
        }
        // Set default checkbox values for Professional Copay
        setValue("facilityProfessionalCopay", false);
        setValue("professionalProfessionalCopay", true);
        trigger("facilityProfessionalCopay");
        trigger("professionalProfessionalCopay");
      }
    };

    window.addEventListener(
      "triggerProfessionalCopay",
      handleProfessionalCopayTrigger as EventListener
    );
    return () => {
      window.removeEventListener(
        "triggerProfessionalCopay",
        handleProfessionalCopayTrigger as EventListener
      );
    };
  }, [showProfessionalBenefits, setValue, trigger, account]);

  const getReductionText = () => {
    if (deductionSuggested > 0 && oopSuggested > 0) {
      return (
        `Reduce deductible by $${deductionSuggested.toFixed(2)}` +
        ` and out-of-pocket max by $${oopSuggested.toFixed(2)}`
      );
    }
    return `Reduce deductible by $${deductionSuggested.toFixed(2)}`;
  };

  const allDeductibleFieldsNull = useMemo(() => {
    if (!benefitDetailsField) return false;
    const deductibleFieldIsNull =
      benefitDetailsField?.response?.remainingDeductible === null;
    const familyCalendarYearDeductibleIsNull =
      benefitDetailsField?.response?.familyCalendarYearDeductible === null;
    const individualCalendarYearDeductibleIsNull =
      benefitDetailsField?.response?.individualCalendarYearDeductible === null;
    return (
      deductibleFieldIsNull &&
      familyCalendarYearDeductibleIsNull &&
      individualCalendarYearDeductibleIsNull
    );
  }, [benefitDetailsField]);

  const coinsuranceFieldIsNull = useMemo(() => {
    if (!benefitDetailsField) return false;
    return benefitDetailsField?.response?.coinsurancePercent === null;
  }, [benefitDetailsField]);

  const isZeroCoinsuranceUnlikely = useMemo(() => {
    if (!benefitDetailsField) return false;
    return !!benefitDetailsField?.response?.isZeroCoinsuranceUnlikely;
  }, [benefitDetailsField]);

  // Some payers report a copay we know to be wrong for this setting, so the server corrects
  // it. Explain the corrected number rather than silently contradicting the payer (CLE-2540).
  const flatCopayOverrideReason = useMemo(
    () => benefitDetailsField?.response?.flatCopayOverride?.reason ?? null,
    [benefitDetailsField]
  );

  // Medicaid payers have their benefits forced to $0 (no patient responsibility).
  // Surface a warning so staff know the $0s are intentional and can override if wrong (CLE-2466).
  const isMedicaidPayer = useMemo(() => {
    const payerType = payersTypeInfo.find(
      (payer: { name: string; type: string }) => payer.name === payerNameField
    )?.type;
    return payerType === payerTypeConstants.MEDICAID;
  }, [payersTypeInfo, payerNameField]);

  const remainingDeductibleOptionCount = useMemo(() => {
    const otherOptions =
      benefitDetailsField?.response?.details?.otherDeductibleOptions ?? [];
    if (otherOptions.length === 0) return 0;

    const distinctValues = new Set<number>();
    const selected = benefitDetailsField?.response?.remainingDeductible;
    if (typeof selected === "number") distinctValues.add(selected);

    otherOptions.forEach((option: { Value?: string }) => {
      if (!option?.Value) return;
      const parsed = parseFloat(option.Value.replace(/[$,]/g, ""));
      if (!Number.isNaN(parsed)) distinctValues.add(parsed);
    });

    return distinctValues.size;
  }, [benefitDetailsField]);

  // Get count of matching orders
  const orderCount = matchingOrdersData.length;

  if (hideBenefits) {
    return null;
  }
  return (
    <div className="w-full flex flex-column">
      <StyledBenefitsContainer className="w-full flex flex-column xl:flex-row gap-4 px-3">
        <div className="w-full flex-column md:flex-row xl:w-5 flex pt-5">
          <StyledBenefitsContainerLeft
            className="flex flex-column w-full  gap-3 mb-4"
            onFocusCapture={(e) =>
              snapshotBenefitField((e.target as HTMLInputElement).id)
            }
            onBlur={(e) =>
              maybeReportBenefitChange((e.target as HTMLInputElement).id)
            }
          >
            <div className="flex flex-column w-full gap-2">
              <div className="flex gap-2 w-full align-items-center">
                <Label htmlFor="deductible">Remaining Deductible</Label>
                {allDeductibleFieldsNull && (
                  <>
                    <Tooltip
                      target=".info-icon"
                      style={{
                        fontSize: "14px",
                        width: "250px",
                      }}
                    />
                    <i
                      className="pi pi-info-circle info-icon"
                      data-pr-tooltip="Deductible info is missing. Please confirm with the payer that this plan has no deductible."
                      style={{ color: "var(--red-600)" }}
                    />
                  </>
                )}
              </div>

              <Controller
                name="deductible"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <StyledInputNumber
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "deductible",
                        trigger,
                      })
                    }
                    inputId="deductible"
                    onValueChange={({ value }) => {
                      onChange(value);
                      setValue("benefitsUpdatedAt", new Date().toISOString());
                    }}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    aria-autocomplete="none"
                    className="w-full"
                  />
                )}
              />
              {remainingDeductibleOptionCount > 1 && (
                <Message
                  severity="warn"
                  className="mt-2"
                  text={`${remainingDeductibleOptionCount} remaining deductible values are available, please review.`}
                />
              )}
            </div>
            <div className="flex flex-column w-full gap-2">
              <Label htmlFor="outOfPocketMax">
                Remaining Out-of-pocket Max
              </Label>

              <Controller
                name="outOfPocketMax"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <StyledInputNumber
                    value={isRemainingOutOfPocketMaxInfinity ? null : value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    disabled={isRemainingOutOfPocketMaxInfinity}
                    placeholder={
                      isRemainingOutOfPocketMaxInfinity
                        ? "No maximum"
                        : undefined
                    }
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "outOfPocketMax",
                        trigger,
                      })
                    }
                    inputId="outOfPocketMax"
                    onValueChange={({ value }) => {
                      onChange(value);
                      setValue("benefitsUpdatedAt", new Date().toISOString());
                    }}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    aria-autocomplete="none"
                  />
                )}
              />
            </div>
            <div className="flex flex-column w-full gap-2">
              <Label htmlFor="flatCopay">Flat Copay</Label>
              <Controller
                name="flatCopay"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <StyledInputNumber
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "flatCopay",
                        trigger,
                      })
                    }
                    inputId="flatCopay"
                    onValueChange={({ value }) => {
                      if (redo) {
                        setValue("redoOrderFirstRender", false);
                      }
                      if (draft) {
                        setValue("draftOrderFirstRender", false);
                      }
                      if (copy) {
                        setValue("copyOrderFirstRender", false);
                      }
                      onChange(value);
                      setValue("benefitsUpdatedAt", new Date().toISOString());
                    }}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    aria-autocomplete="none"
                    className="w-full"
                  />
                )}
              />
              {flatCopayOverrideReason && (
                <Message
                  severity="warn"
                  className="mt-2"
                  text={flatCopayOverrideReason}
                />
              )}
              {showProfessionalBenefits &&
                account.costApplicationRulesVisible.nonFacilityRules && (
                  <button
                    type="button"
                    className="text-sm text-primary-600 no-underline border-none bg-transparent cursor-pointer text-left"
                    onClick={(e) => {
                      e.preventDefault();
                      const newState = !showProfessionalCopay;
                      setShowProfessionalCopay(newState);
                      setValue("showProfessionalCopay", newState);
                      // Auto-uncheck Prof checkbox only when showing (+Prof Copay)
                      if (newState && showProfessionalBenefits) {
                        setValue("professionalCopay", false);
                        trigger("professionalCopay");
                      }
                      // Set default checkbox values for Professional Copay when showing
                      if (newState) {
                        setValue("facilityProfessionalCopay", false);
                        setValue("professionalProfessionalCopay", true);
                        trigger("facilityProfessionalCopay");
                        trigger("professionalProfessionalCopay");
                      }
                    }}
                  >
                    {showProfessionalCopay ? "-Prof Copay" : "+Prof Copay"}
                  </button>
                )}
              {showProfessionalCopay && (
                <div className="mt-2">
                  <Label htmlFor="professionalFlatCopay">
                    Professional Copay
                  </Label>
                  <Controller
                    name="professionalFlatCopay"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <StyledInputNumber
                        value={value}
                        currency="USD"
                        locale="en-US"
                        mode="currency"
                        onBlur={() =>
                          handleOnBlurField({
                            onBlur,
                            field: "professionalFlatCopay",
                            trigger,
                          })
                        }
                        inputId="professionalFlatCopay"
                        onValueChange={({ value }) => {
                          if (redo) {
                            setValue("redoOrderFirstRender", false);
                          }
                          if (draft) {
                            setValue("draftOrderFirstRender", false);
                          }
                          if (copy) {
                            setValue("copyOrderFirstRender", false);
                          }
                          setValue(
                            "benefitsUpdatedAt",
                            new Date().toISOString()
                          );
                          onChange(value);
                        }}
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        min={0}
                        aria-autocomplete="none"
                        className="w-full"
                      />
                    )}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-column w-full gap-2">
              <div className="flex gap-2 w-full align-items-center">
                <Label htmlFor="coinsurancePercent">Coinsurance Percent</Label>
                {coinsuranceFieldIsNull && (
                  <>
                    <Tooltip
                      target=".info-icon"
                      style={{
                        fontSize: "14px",
                        width: "250px",
                      }}
                    />
                    <i
                      className="pi pi-info-circle info-icon"
                      data-pr-tooltip="Coinsurance info is missing. Please confirm with the payer that this plan has no coinsurance."
                      style={{ color: "var(--red-600)" }}
                    />
                  </>
                )}
              </div>

              <Controller
                name="coinsurancePercent"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <StyledInputNumber
                    value={value}
                    suffix="%"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "coinsurancePercent",
                        trigger,
                      })
                    }
                    inputId="coinsurancePercent"
                    onValueChange={({ value }) => {
                      onChange(value);
                      setValue("benefitsUpdatedAt", new Date().toISOString());
                    }}
                    min={0}
                    max={100}
                    aria-autocomplete="none"
                    className="w-full"
                  />
                )}
              />
              {isZeroCoinsuranceUnlikely && (
                <Message
                  severity="warn"
                  className="mt-2"
                  text="0% coinsurance is unlikely for commercial insurance. Please confirm the correct rate from other sources before continuing."
                />
              )}
              {isMedicaidPayer && (
                <Message
                  severity="warn"
                  className="mt-2"
                  text="Benefits are set to $0 because this payer is configured as Medicaid. If this is incorrect, please update the benefits."
                />
              )}
            </div>
            {isSuperAdmin && (
              <Button
                type="button"
                icon="pi pi-flag"
                label="Report benefits issue"
                className="p-button-link p-0 text-sm align-self-start"
                onClick={() =>
                  setReportModal({ visible: true, source: "MANUAL_BUTTON" })
                }
              />
            )}
          </StyledBenefitsContainerLeft>
          {(showFacilityBenefits || showProfessionalBenefits) && (
            <StyledBenefitsContainerRight className="flex w-full flex-column md:flex-row gap-3">
              {showFacilityBenefits && (
                <div className="flex flex-column w-full align-items-center md:w-6">
                  <div className="flex gap-2">
                    <Label className="mb-2" htmlFor="facilityBenefits">
                      Fac
                    </Label>
                    <StyledTooltip
                      target=".benefits-info"
                      style={{
                        fontSize: "14px",
                        width: "320px",
                      }}
                    >
                      <span className="text-gray-900">
                        {facilityBenefitsInfo.title}
                      </span>
                      <ul className="mt-2">
                        {facilityBenefitsInfo.items.map((item, index) => (
                          <li
                            className="flex gap-2 mb-2"
                            key={`${item.title}_${index}`}
                          >
                            <p className="font-normal italic text-gray-900">
                              <span className="font-bold text-gray-900">
                                {item.title}:
                              </span>{" "}
                              {item.info}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </StyledTooltip>
                    <i
                      className="benefits-info pi pi-info-circle"
                      data-pr-tooltip=""
                      style={{
                        color: "var(--primary-color)",
                        marginTop: "2px",
                      }}
                    />
                  </div>
                  <div className="flex w-full pt-2 pb-3 justify-content-center ">
                    <Controller
                      name="facilityDeductible"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="facilityDeductible"
                            className="flex md:hidden"
                          >
                            Deductible
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="facilityDeductible"
                            name="facilityDeductible"
                          />
                        </div>
                      )}
                    />
                  </div>
                  <StyledCheckboxContainer
                    $extraTopSpace={remainingDeductibleOptionCount > 1}
                    className="flex w-full pt-2 pb-3 justify-content-center"
                  >
                    <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                      <Label
                        htmlFor="facilityOutOfPocketMax"
                        className="flex md:hidden"
                      >
                        Out-of-pocket Max
                      </Label>
                      <Checkbox
                        className="ml-3"
                        onChange={() => {}}
                        disabled
                        checked
                      />
                    </div>
                  </StyledCheckboxContainer>
                  <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                    <Controller
                      name="facilityCopay"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="facilityCopay"
                            className="flex md:hidden"
                          >
                            Copay
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="facilityCopay"
                            name="facilityCopay"
                          />
                        </div>
                      )}
                    />
                  </StyledCheckboxContainer>
                  {showProfessionalCopay && (
                    <StyledCoinsuranceCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                      <Controller
                        name="facilityProfessionalCopay"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                            <Label
                              htmlFor="facilityProfessionalCopay"
                              className="flex md:hidden"
                            >
                              Professional Copay
                            </Label>
                            <Checkbox
                              className="ml-3"
                              onChange={onChange}
                              checked={value}
                              inputId="facilityProfessionalCopay"
                              name="facilityProfessionalCopay"
                            />
                          </div>
                        )}
                      />
                    </StyledCoinsuranceCheckboxContainer>
                  )}
                  <StyledCoinsuranceCheckboxContainer
                    className="flex w-full pt-2 pb-3 justify-content-center"
                    $compact={showProfessionalCopay}
                  >
                    <Controller
                      name="facilityCoinsurance"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="facilityCoinsurance"
                            className="flex md:hidden"
                          >
                            Coinsurance
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="facilityCoinsurance"
                            name="facilityCoinsurance"
                          />
                        </div>
                      )}
                    />
                  </StyledCoinsuranceCheckboxContainer>
                </div>
              )}
              {showProfessionalBenefits && (
                <div className="flex flex-column w-full align-items-center md:w-6">
                  <div className="flex gap-2">
                    <Label className="mb-2" htmlFor="professionalBenefits">
                      Prof
                    </Label>
                    <StyledTooltip
                      target=".prof-info"
                      style={{
                        fontSize: "14px",
                        width: "320px",
                      }}
                    >
                      <span className="text-gray-900 mb-2">
                        {professionalBenefitsInfo.title}
                      </span>
                      <ul className="mt-2">
                        {professionalBenefitsInfo.items.map((item, index) => (
                          <li
                            className="flex gap-2 mb-2"
                            key={`${item.title}_${index}`}
                          >
                            <p className="font-normal italic text-gray-900">
                              <span className="font-bold text-gray-900">
                                {item.title}:
                              </span>{" "}
                              {item.info}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </StyledTooltip>
                    <i
                      className="prof-info pi pi-info-circle"
                      data-pr-tooltip=""
                      style={{
                        color: "var(--primary-color)",
                        marginTop: "2px",
                      }}
                    />
                  </div>
                  <div className="flex w-full pt-2 pb-3 justify-content-center">
                    <Controller
                      name="professionalDeductible"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="professionalDeductible"
                            className="flex md:hidden"
                          >
                            Deductible
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="professionalDeductible"
                            name="professionalDeductible"
                          />
                        </div>
                      )}
                    />
                  </div>
                  <StyledCheckboxContainer
                    $extraTopSpace={remainingDeductibleOptionCount > 1}
                    className="flex w-full pt-2 pb-3 justify-content-center"
                  >
                    <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                      <Label
                        htmlFor="professionalOutOfPocketMax"
                        className="flex md:hidden"
                      >
                        Out-of-pocket Max
                      </Label>
                      <Checkbox
                        className="ml-3"
                        onChange={() => {}}
                        disabled
                        checked
                      />
                    </div>
                  </StyledCheckboxContainer>
                  <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                    <Controller
                      name="professionalCopay"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="professionalCopay"
                            className="flex md:hidden"
                          >
                            Copay
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="professionalCopay"
                            name="professionalCopay"
                          />
                        </div>
                      )}
                    />
                  </StyledCheckboxContainer>
                  {showProfessionalCopay && (
                    <StyledCoinsuranceCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                      <Controller
                        name="professionalProfessionalCopay"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                            <Label
                              htmlFor="professionalProfessionalCopay"
                              className="flex md:hidden"
                            >
                              Professional Copay
                            </Label>
                            <Checkbox
                              className="ml-3"
                              onChange={onChange}
                              checked={value}
                              inputId="professionalProfessionalCopay"
                              name="professionalProfessionalCopay"
                            />
                          </div>
                        )}
                      />
                    </StyledCoinsuranceCheckboxContainer>
                  )}
                  <StyledCoinsuranceCheckboxContainer
                    $compact={showProfessionalCopay}
                    className="flex w-full pt-2 pb-3 justify-content-center"
                  >
                    <Controller
                      name="professionalCoinsurance"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="professionalCoinsurance"
                            className="flex md:hidden"
                          >
                            Coinsurance
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="professionalCoinsurance"
                            name="professionalCoinsurance"
                          />
                        </div>
                      )}
                    />
                  </StyledCoinsuranceCheckboxContainer>
                </div>
              )}
            </StyledBenefitsContainerRight>
          )}
        </div>
        <PatientBenefits
          benefitDetailsField={benefitDetailsField}
          eligibilityMessage={eligibilityMessage}
          deductibleField={deductibleField}
          outOfPocketMaxField={outOfPocketMaxField}
          payerType={
            payersTypeInfo.find(
              (payer: { name: string; type: string }) =>
                payer.name === payerNameField
            )?.type
          }
          handleChangeDeductibleField={(value: number) => {
            setValue("deductible", value);
            setValue("benefitsUpdatedAt", new Date().toISOString());
          }}
          handleChangeOOPField={(value: number) => {
            setValue("outOfPocketMax", value);
            setValue("benefitsUpdatedAt", new Date().toISOString());
          }}
          coinsuranceField={coinsuranceField}
          handleChangeCoinsuranceField={(value: number) => {
            setValue("coinsurancePercent", value);
            setValue("benefitsUpdatedAt", new Date().toISOString());
          }}
        />
      </StyledBenefitsContainer>
      {hasMatchingOrders && (
        <MatchingOrders
          items={matchingOrdersData}
          severity={reductionsApplied ? "success" : "warn"}
          textTop={
            reductionsApplied
              ? `Successfully applied reductions to benefits`
              : `Found ${orderCount} previous order${
                  orderCount !== 1 ? "s" : ""
                } for this patient.`
          }
          textBottom={reductionsApplied ? `` : `${getReductionText()}`}
          buttonLabel={reductionsApplied ? "Undo" : "Apply Reductions"}
          onButtonClick={reductionsApplied ? undoReductions : applyReductions}
        />
      )}
      {isSuperAdmin && (
        <ReportBenefitsModal
          account={account}
          visible={reportModal.visible}
          initialField={reportModal.field}
          source={reportModal.source}
          autoValue={reportModal.autoValue}
          correctedValue={reportModal.correctedValue}
          onHide={() => setReportModal((prev) => ({ ...prev, visible: false }))}
        />
      )}
    </div>
  );
}
